import React, { useState } from 'react';
import { DEFICIENCY_CHOICES, REMEDIAL_ACTION_CHOICES, type DraftConcern } from '../../types';
import { today, localId } from '../../utils/scheduling';

interface Props {
  area: string;
  areaType: string;
  inspector: string;
  supervisor: string;
  inspectionDate: string;
  isOnline: boolean;
  onSave: (concern: Omit<DraftConcern, 'localId' | 'parentDraftId'>) => void;
  onClose: () => void;
}

interface FormState {
  deficiency: string;
  remedialAction: string;
  spadBT: string;
  remediated: string;
  remediatedDate: string;
  comment: string;
}

const EMPTY_FORM: FormState = {
  deficiency: '',
  remedialAction: '',
  spadBT: '',
  remediated: '',
  remediatedDate: '',
  comment: '',
};

export default function DeficiencyModal({
  area,
  areaType,
  inspector,
  supervisor,
  inspectionDate,
  onSave,
  onClose,
}: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const set = (field: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: '' }));
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.deficiency) errs.deficiency = 'Deficiency is required';
    if (!form.remedialAction) errs.remedialAction = 'Remedial action is required';
    if (!form.spadBT.trim()) errs.spadBT = 'Spad/BT is required';
    if (!form.remediated) errs.remediated = 'Remediated status is required';
    if (form.remediated === 'Yes' && !form.remediatedDate)
      errs.remediatedDate = 'Remediated date is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({
      Area: area,
      AreaType: areaType,
      Deficiency: form.deficiency,
      RemedialAction: form.remedialAction,
      SpadBT: form.spadBT,
      Remediated: form.remediated,
      RemediatedDate: form.remediated === 'Yes' ? form.remediatedDate : undefined,
      Comment: form.comment,
      Inspector: inspector,
      Supervisor: supervisor,
      InspectionDate: inspectionDate || today(),
      Status: form.remediated === 'Yes' ? 'Closed' : 'Open',
    });
  };

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto slide-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#4C9E00]">Add Deficiency</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Location context */}
        <div className="mx-5 mt-4 mb-3 bg-[#EBF7E0] rounded-lg px-3 py-2 text-sm text-[#4C9E00] font-medium">
          {area} · {areaType}
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* Deficiency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deficiency <span className="text-red-500">*</span>
            </label>
            <select
              value={form.deficiency}
              onChange={(e) => set('deficiency', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${
                errors.deficiency ? 'border-red-400' : 'border-gray-300'
              }`}
            >
              <option value="">Select deficiency…</option>
              {DEFICIENCY_CHOICES.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {errors.deficiency && (
              <p className="text-red-500 text-xs mt-1">{errors.deficiency}</p>
            )}
          </div>

          {/* Remedial Action */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remedial Action <span className="text-red-500">*</span>
            </label>
            <select
              value={form.remedialAction}
              onChange={(e) => set('remedialAction', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${
                errors.remedialAction ? 'border-red-400' : 'border-gray-300'
              }`}
            >
              <option value="">Select action…</option>
              {REMEDIAL_ACTION_CHOICES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {errors.remedialAction && (
              <p className="text-red-500 text-xs mt-1">{errors.remedialAction}</p>
            )}
          </div>

          {/* Spad/BT */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spad / BT # <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.spadBT}
              onChange={(e) => set('spadBT', e.target.value)}
              placeholder="e.g. BT-2025-001"
              className={`w-full border rounded-lg px-3 py-2 text-sm ${
                errors.spadBT ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {errors.spadBT && (
              <p className="text-red-500 text-xs mt-1">{errors.spadBT}</p>
            )}
          </div>

          {/* Remediated */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remediated? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              {['Yes', 'No'].map((opt) => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="remediated"
                    value={opt}
                    checked={form.remediated === opt}
                    onChange={() => set('remediated', opt)}
                    className="accent-[#4C9E00] w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">{opt}</span>
                </label>
              ))}
            </div>
            {errors.remediated && (
              <p className="text-red-500 text-xs mt-1">{errors.remediated}</p>
            )}
          </div>

          {/* Remediated Date (conditional) */}
          {form.remediated === 'Yes' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remediated Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.remediatedDate}
                onChange={(e) => set('remediatedDate', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${
                  errors.remediatedDate ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              {errors.remediatedDate && (
                <p className="text-red-500 text-xs mt-1">{errors.remediatedDate}</p>
              )}
            </div>
          )}

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comments
            </label>
            <textarea
              value={form.comment}
              onChange={(e) => set('comment', e.target.value)}
              rows={2}
              placeholder="Additional notes…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 bg-[#4C9E00] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#3D8000] transition"
            >
              Add Deficiency
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
